import { useQuery } from "@tanstack/react-query";

import { api, type ReportScope } from "../../../lib/api";
import {
  withCardFilters,
  type DashboardLayout,
} from "../../../lib/dashboard-layout";
import { queryKeys } from "../../../lib/queryKeys";
import { DASHBOARD_STALE_MS } from "../../../lib/dashboard-refresh";
import { rangeIsReady, toRangeValue } from "../RangeFilter";
import { CardChart, ReportCard } from "../card/ReportCard";
import { TicketCount } from "../card/TicketCount";
import { CATEGORY_CHARTS } from "../card/chart-types";

/**
 * Where the work comes from: tickets raised, by pipeline.
 *
 * Not the same question as the segment report, which asks where the desk is
 * slow. A ticket raised in Support and eventually answered in Finance is one
 * row here under Support and a segment there under Finance, and both are the
 * right answer to their own question.
 *
 * Series are the pipelines themselves, so the colour panel offers one swatch
 * per pipeline and each starts from the chip colour a manager already chose —
 * the colour the rest of Pulse draws that pipeline in.
 */
export function PipelineReport({
  layout,
  scope: pageScope,
  isManager,
}: {
  layout: DashboardLayout;
  /** Only a manager is offered the Agent filter; the server enforces it. */
  isManager: boolean;
  scope: ReportScope;
}) {
  const stored = layout.card("pipelines");
  /* The card's own Pipeline and Agent filters, folded into the page's
     scope so they reach both the query and its key. */
  const scope = withCardFilters(pageScope, stored);
  const range = toRangeValue(stored.range, { preset: "this-month" });
  const ready = rangeIsReady(range);

  const report = useQuery({
    queryKey: queryKeys.dashboardReport("pipeline-report", range, scope),
    queryFn: () => api.pipelineReport(range, scope),
    enabled: ready,
    staleTime: DASHBOARD_STALE_MS,
  });

  const breakdown = report.data?.breakdown ?? [];

  /*
   * Keyed on the name rather than the id, because the donut colours its slices
   * by row label — a pipeline has to resolve to the same colour whether it is
   * a bar or a slice, or switching chart type would reshuffle the chart.
   */
  const series = breakdown.map((row) => ({
    key: row.name,
    label: row.name,
    color: row.color,
  }));

  return (
    <ReportCard
      id="pipelines"
      title="Tickets by pipeline"
      subtitle="Raised in the period, against the same span before it"
      layout={layout}
      offerAgents={isManager}
      charts={CATEGORY_CHARTS}
      series={series}
      footnote={
        <>
          {/* Pulse's deduplication: two mails about one problem, merged. Stated
              so the total reconciles with what anybody counts by hand. */}
          {report.data?.mergedCount ? (
            <p>{report.data.mergedCount} duplicates counted once.</p>
          ) : null}
          {report.data?.needsPipeline ? (
            <p>
              {report.data.needsPipeline} with no pipeline — triage debt rather
              than a category, so not shown as one.
            </p>
          ) : null}
        </>
      }
    >
      {({ chart, colorFor }) => {
        if (!ready) {
          return (
            <p className="py-6 text-center text-xs text-faint">
              Pick both dates.
            </p>
          );
        }

        const total = report.data?.total ?? 0;

        return (
          <div className="space-y-2">
            <p className="text-xs text-muted">
              <span className="text-lg font-semibold tabular-nums text-content">
                {total.toLocaleString()}
              </span>{" "}
              raised
            </p>

            <CardChart
              chart={chart}
              /* One row per pipeline. On a bar chart the single series is the
                 count; on a donut each row is a slice. */
              rows={breakdown.map((row) => ({
                label: row.name,
                values: { count: row.count },
              }))}
              series={[{ key: "count", label: "Tickets" }]}
              /* Table view only: a count that opens into the tickets behind
                 it. Keyed off the row label because that is what the chart
                 draws; the id comes back out of `breakdown`. */
              renderValue={(row, _key, value) => (
                <TicketCount
                  count={value ?? 0}
                  dimension="raised-pipeline"
                  value={String(
                    breakdown.find((entry) => entry.name === row.label)
                      ?.pipelineId ?? "",
                  )}
                  label={`${row.label} — raised in this period`}
                  range={range}
                  scope={scope}
                />
              )}
              /* The categories are the pipelines, so each bar or slice takes
                 its own colour — and keeps it when the chart type changes. */
              colorBy="row"
              colorFor={colorFor}
              format={(value) => (value === null ? "—" : value.toLocaleString())}
              emptyMessage={
                report.isLoading ? "Loading…" : "Nothing was raised in this period."
              }
            />
          </div>
        );
      }}
    </ReportCard>
  );
}
